import { useEffect, useState } from "react";
import { Card } from "../components/Card";
import { Eyebrow } from "../components/Eyebrow";

// 管理画面: API キー設定（先方アカウントへの差し替え）
// 対応: docs/plans/2026-07-16-admin-api-keys-design.md §D-6
// 生キーは表示しない。状態（configured/source/masked）のみ扱う。

type SecretStatus = {
  name: "openai" | "gemini";
  configured: boolean;
  source: "db" | "secret" | "none";
  masked: string | null;
  updated_by: string | null;
  updated_at: string | null;
};

const LABELS: Record<SecretStatus["name"], { title: string; hint: string }> = {
  openai: { title: "OpenAI（ChatGPT 画像生成）", hint: "gpt-image-1 の API キー" },
  gemini: { title: "Gemini（Google 画像生成）", hint: "gemini-2.5-flash-image の API キー" },
};

function sourceBadge(s: SecretStatus) {
  if (s.source === "db")
    return { text: "先方のキー使用中", cls: "bg-emerald-500/10 text-emerald-700 ring-emerald-500/30" };
  if (s.source === "secret")
    return { text: "自社のキー使用中", cls: "bg-ink/5 text-ink-muted ring-ink/15" };
  return { text: "未設定", cls: "bg-red-500/10 text-red-700 ring-red-500/30" };
}

export function AdminSecrets() {
  const [secrets, setSecrets] = useState<SecretStatus[]>([]);
  const [encAvailable, setEncAvailable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null); // 処理中の name
  const [msg, setMsg] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/admin-secrets");
      const data = await res.json();
      if (!data.ok) throw new Error(data.error ?? "取得に失敗しました");
      setSecrets(data.secrets ?? []);
      setEncAvailable(data.enc_available !== false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function save(name: string) {
    const value = (inputs[name] ?? "").trim();
    if (!value) return;
    setBusy(name);
    setMsg(null);
    try {
      const res = await fetch("/api/admin-secrets", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, value }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error ?? "保存に失敗しました");
      setInputs((p) => ({ ...p, [name]: "" }));
      setMsg(`✓ ${LABELS[name as SecretStatus["name"]].title} のキーを保存しました`);
      await load();
    } catch (e) {
      setMsg("⚠ " + (e instanceof Error ? e.message : String(e)));
    } finally {
      setBusy(null);
    }
  }

  async function remove(name: string) {
    setBusy(name);
    setMsg(null);
    try {
      const res = await fetch(`/api/admin-secrets?name=${name}`, { method: "DELETE" });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error ?? "解除に失敗しました");
      setMsg(`✓ ${LABELS[name as SecretStatus["name"]].title} を自社キーに戻しました`);
      await load();
    } catch (e) {
      setMsg("⚠ " + (e instanceof Error ? e.message : String(e)));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <Eyebrow>Settings — API キー設定</Eyebrow>
        <h2 className="text-lg font-semibold tracking-tight mt-1">画像生成の API アカウント</h2>
        <p className="text-sm text-ink-muted mt-1 max-w-[60ch]">
          先方（クライアント）の API アカウントに差し替えられます。キーを保存すると即座にそのキーで生成します。
          解除すると自社のキー（システム既定）に戻ります。入力したキーは画面に再表示されません。
        </p>
      </div>

      {!encAvailable && (
        <div className="px-4 py-3 ring-1 ring-amber-400 bg-amber-50 text-sm text-amber-800 rounded">
          暗号化鍵（SETTINGS_ENC_KEY）が未設定のため、キーの保存はできません。管理者にご連絡ください（現在は自社キーで動作）。
        </div>
      )}
      {error && (
        <div className="px-4 py-3 ring-1 ring-red-300 bg-red-50 text-sm text-red-800 rounded">
          取得エラー: {error}
        </div>
      )}
      {msg && (
        <div className="px-4 py-2 ring-1 ring-ink/10 bg-white text-sm rounded">{msg}</div>
      )}

      {loading ? (
        <p className="text-sm text-ink-muted">読み込み中…</p>
      ) : (
        <div className="space-y-4">
          {secrets.map((s) => {
            const badge = sourceBadge(s);
            const label = LABELS[s.name];
            return (
              <Card key={s.name} className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-sm font-semibold">{label.title}</h3>
                    <p className="text-xs text-ink-muted mt-0.5">{label.hint}</p>
                  </div>
                  <span className={`shrink-0 text-[11px] px-2.5 py-1 rounded-full ring-1 ${badge.cls}`}>
                    {badge.text}
                  </span>
                </div>

                <div className="mt-3 text-xs text-ink-muted">
                  {s.masked ? (
                    <span>
                      現在のキー: <span className="font-mono text-ink">{s.masked}</span>
                      {s.source === "db" && s.updated_by && (
                        <span className="ml-2">
                          （{s.updated_by} が
                          {s.updated_at ? ` ${new Date(s.updated_at).toLocaleDateString("ja-JP")}` : ""}
                          設定）
                        </span>
                      )}
                    </span>
                  ) : (
                    <span>キーが設定されていません</span>
                  )}
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <input
                    type="password"
                    autoComplete="off"
                    value={inputs[s.name] ?? ""}
                    onChange={(e) => setInputs((p) => ({ ...p, [s.name]: e.target.value }))}
                    placeholder="新しい API キーを貼り付け"
                    disabled={!encAvailable || busy === s.name}
                    className="w-72 ring-1 ring-ink/15 rounded-full px-4 py-2 text-sm font-mono disabled:opacity-50"
                  />
                  <button
                    type="button"
                    onClick={() => save(s.name)}
                    disabled={!encAvailable || busy === s.name || !(inputs[s.name] ?? "").trim()}
                    className="px-4 py-2 rounded-full bg-ink text-white text-sm font-medium hover:bg-ink/90 disabled:opacity-40"
                  >
                    {busy === s.name ? "処理中…" : "先方のキーに差し替え"}
                  </button>
                  {s.source === "db" && (
                    <button
                      type="button"
                      onClick={() => remove(s.name)}
                      disabled={busy === s.name}
                      className="px-4 py-2 rounded-full ring-1 ring-ink/15 bg-white text-sm font-medium hover:ring-ink/40 disabled:opacity-40"
                    >
                      自社キーに戻す
                    </button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
